import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../styles/Header.css';
import { useAuth } from '../context/AuthContext';
import { useLoginModal } from '../context/LoginModalContext';
import { SUPPORT_EMAIL, SUPPORT_WHATSAPP_DISPLAY, SUPPORT_WHATSAPP_USERNAME, whatsappLink, mailtoLink } from '../config/supportContact';

// Generate gradient color based on first letter of name
const getAvatarGradient = (name) => {
  const letter = (name || 'U').charAt(0).toUpperCase();
  const gradients = {
    A: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Purple
    B: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // Pink-Red
    C: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Cyan
    D: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Green-Teal
    E: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // Pink-Yellow
    F: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', // Teal-Purple
    G: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', // Mint-Pink
    H: 'linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)', // Orange-Pink
    I: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', // Purple-Blue
    J: 'linear-gradient(135deg, #f77062 0%, #fe5196 100%)', // Coral-Pink
    K: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)', // Pink-Blue
    L: 'linear-gradient(135deg, #fdcbf1 0%, #e6dee9 100%)', // Light Pink
    M: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)', // Light Blue
    N: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)', // Purple-Yellow
    O: 'linear-gradient(135deg, #f5576c 0%, #ffa500 100%)', // Red-Orange
    P: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Purple
    Q: 'linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)', // Pink-Cyan
    R: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)', // Lavender-Blue
    S: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // Pink-Red
    T: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Cyan
    U: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Green-Teal
    V: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // Pink-Yellow
    W: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', // Teal-Purple
    X: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', // Mint-Pink
    Y: 'linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)', // Orange-Pink
    Z: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)'  // Purple-Blue
  };
  return gradients[letter] || gradients['U'];
};

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { openLoginModal } = useLoginModal();
  const [supportOpen, setSupportOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const supportRef = useRef(null);
  const accountRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  const closeMenu = () => setMenuOpen(false);

  const handleBookFlightsClick = (e) => {
    // Always reset Book Flights page state when this tab is clicked.
    e.preventDefault();
    closeMenu();
    sessionStorage.removeItem('airfly_search_results');
    sessionStorage.removeItem('airfly_search_results_restore_allowed');
    navigate('/', {
      state: {
        resetSearch: true,
        resetAt: Date.now(),
      },
    });
  };

  const handleLogoClick = (e) => {
    // Reset app when logo is clicked - clear all search state and navigate home
    e.preventDefault();
    closeMenu();
    sessionStorage.removeItem('airfly_search_results');
    sessionStorage.removeItem('airfly_search_results_restore_allowed');
    sessionStorage.removeItem('airfly_checkout_preview');
    sessionStorage.removeItem('airfly_stripe_initiated');
    navigate('/', {
      state: {
        resetSearch: true,
        resetAt: Date.now(),
      },
      replace: true, // Replace history to prevent going back to cleared state
    });
  };

  const handleSignOut = () => {
    setShowSignOutConfirm(true);
  };

  const confirmSignOut = () => {
    setShowSignOutConfirm(false);
    closeMenu();
    setAccountOpen(false);
    logout();
    navigate('/');
  };

  const cancelSignOut = () => {
    setShowSignOutConfirm(false);
  };

  // Close mobile menu and dropdowns on route change
  useEffect(() => {
    setMenuOpen(false);
    setSupportOpen(false);
    setAccountOpen(false);
    setShowSignOutConfirm(false);
  }, [location.pathname]);

  // Lock body scroll when mobile drawer is open — uses position:fixed trick for iOS Safari
  useEffect(() => {
    if (menuOpen) {
      const scrollY = window.scrollY;
      document.documentElement.style.setProperty('--scroll-y', `${scrollY}px`);
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add('nav-open');
    } else {
      const scrollY = Math.abs(parseInt(document.body.style.top || '0', 10));
      document.body.classList.remove('nav-open');
      document.body.style.top = '';
      if (scrollY) window.scrollTo(0, scrollY);
    }
    return () => {
      document.body.classList.remove('nav-open');
      document.body.style.top = '';
    };
  }, [menuOpen]);

  // Lock body scroll when sign-out modal is open
  useEffect(() => {
    if (showSignOutConfirm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showSignOutConfirm]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (supportRef.current && !supportRef.current.contains(e.target)) {
        setSupportOpen(false);
      }
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setSupportOpen(false);
        setAccountOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <header className="header">
      <div className="header-content">
        <Link to="/" className="logo" aria-label="AirFlyNow – go to homepage" onClick={handleLogoClick}>
          <img
            src="/branding/logo-display.png"
            alt="AirFlyNow"
            className="logo-image"
          />
        </Link>

        {/* Hamburger — visible on mobile only */}
        <button
          type="button"
          className={`nav-hamburger${menuOpen ? ' nav-hamburger--open' : ''}`}
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
          aria-controls="main-nav"
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>

        {/* Backdrop — only rendered when drawer is open */}
        {menuOpen && (
          <div
            className="nav-backdrop"
            aria-hidden="true"
            onClick={closeMenu}
          />
        )}

        <nav
          id="main-nav"
          className={`nav${menuOpen ? ' nav--open' : ''}`}
          aria-label="Main navigation"
        >
          <Link
            to="/"
            className={`nav-link ${isActive('/') ? 'active' : ''}`}
            onClick={handleBookFlightsClick}
          >
            Book Flights
          </Link>
          <Link
            to="/hotels"
            className={`nav-link ${isActive('/hotels') ? 'active' : ''}`}
            onClick={closeMenu}
          >
            Hotels
          </Link>
          {/* COMMENTED OUT FOR PRODUCTION - UNCOMMENT AFTER DEPLOYMENT */}
          {/* <Link
            to="/air-radar"
            className={`nav-link ${isActive('/air-radar') ? 'active' : ''}`}
            onClick={closeMenu}
          >
            AIR Radar
          </Link> */}
          {isAuthenticated && (
            <Link
              to="/my-bookings"
              className={`nav-link ${isActive('/my-bookings') ? 'active' : ''}`}
              onClick={closeMenu}
            >
              My Bookings
            </Link>
          )}

          {/* Support dropdown */}
          <div className="support-dropdown" ref={supportRef}>
            <button
              type="button"
              className={`nav-link support-toggle ${supportOpen ? 'active' : ''}`}
              onClick={() => setSupportOpen((o) => !o)}
              aria-expanded={supportOpen}
              aria-haspopup="menu"
            >
              Support
              <span className="support-chevron" aria-hidden="true" />
            </button>
            {supportOpen && (
              <div className="support-popover" role="menu" aria-label="Support options">
                <p className="support-popover__heading">Get Help</p>
                <Link
                  to="/support"
                  className="support-popover__item"
                  role="menuitem"
                  onClick={() => { setSupportOpen(false); closeMenu(); }}
                >
                  <span className="support-popover__icon" aria-hidden="true" style={{ fontSize: '20px' }}>
                    🧭
                  </span>
                  <span className="support-popover__body">
                    <span className="support-popover__label">Help Center</span>
                    <span className="support-popover__value">Guides for cancellations, refunds & more</span>
                  </span>
                </Link>
                <Link
                  to="/faq"
                  className="support-popover__item"
                  role="menuitem"
                  onClick={() => { setSupportOpen(false); closeMenu(); }}
                >
                  <span className="support-popover__icon" aria-hidden="true" style={{ fontSize: '20px' }}>
                    ❓
                  </span>
                  <span className="support-popover__body">
                    <span className="support-popover__label">FAQ</span>
                    <span className="support-popover__value">Common questions answered</span>
                  </span>
                </Link>
                <Link
                  to="/contact"
                  className="support-popover__item"
                  role="menuitem"
                  onClick={() => { setSupportOpen(false); closeMenu(); }}
                >
                  <span className="support-popover__icon" aria-hidden="true" style={{ fontSize: '20px' }}>
                    💬
                  </span>
                  <span className="support-popover__body">
                    <span className="support-popover__label">Contact Form</span>
                    <span className="support-popover__value">Send us a message</span>
                  </span>
                </Link>
                <Link
                  to="/about"
                  className="support-popover__item"
                  role="menuitem"
                  onClick={() => { setSupportOpen(false); closeMenu(); }}
                >
                  <span className="support-popover__icon" aria-hidden="true" style={{ fontSize: '20px' }}>
                    ℹ️
                  </span>
                  <span className="support-popover__body">
                    <span className="support-popover__label">About Us</span>
                    <span className="support-popover__value">Learn about AirFlyNow</span>
                  </span>
                </Link>
                <Link
                  to="/carriers"
                  className="support-popover__item"
                  role="menuitem"
                  onClick={() => { setSupportOpen(false); closeMenu(); }}
                >
                  <span className="support-popover__icon" aria-hidden="true" style={{ fontSize: '20px' }}>
                    ✈️
                  </span>
                  <span className="support-popover__body">
                    <span className="support-popover__label">Airline Directory</span>
                    <span className="support-popover__value">Browse supported airlines</span>
                  </span>
                </Link>
                <a
                  className="support-popover__item"
                  role="menuitem"
                  href={mailtoLink()}
                  onClick={() => { setSupportOpen(false); closeMenu(); }}
                >
                  <span className="support-popover__icon" aria-hidden="true" style={{ fontSize: '20px' }}>
                    ✉️
                  </span>
                  <span className="support-popover__body">
                    <span className="support-popover__label">Email Support</span>
                    <span className="support-popover__value">{SUPPORT_EMAIL}</span>
                  </span>
                </a>
                <a
                  className="support-popover__item"
                  role="menuitem"
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { setSupportOpen(false); closeMenu(); }}
                >
                  <span className="support-popover__icon" aria-hidden="true" style={{ fontSize: '20px' }}>
                    💬
                  </span>
                  <span className="support-popover__body">
                    <span className="support-popover__label">WhatsApp Support</span>
                    <span className="support-popover__value">{SUPPORT_WHATSAPP_DISPLAY} · {SUPPORT_WHATSAPP_USERNAME}</span>
                  </span>
                </a>
                <p className="support-popover__hours">Available 24/7</p>
              </div>
            )}
          </div>

          {isAuthenticated ? (
            <div className="support-dropdown" ref={accountRef}>
              <button
                type="button"
                className={`session-pill ${accountOpen ? 'active' : ''}`}
                onClick={() => setAccountOpen((o) => !o)}
                aria-expanded={accountOpen}
                aria-haspopup="menu"
                aria-label="Account menu"
              >
                <div 
                  className="session-avatar" 
                  style={{ background: getAvatarGradient(user?.fullName) }}
                >
                  {(user?.fullName || 'U').charAt(0).toUpperCase()}
                </div>
                <div className="session-meta">
                  <span className="session-name">{user?.fullName || 'Traveler'}</span>
                  <span className="session-email">{user?.email}</span>
                </div>
                <div className="session-chevron-icon" style={{ fontSize: '14px' }}>
                  ▼
                </div>
              </button>
              {accountOpen && (
                <div className="support-popover" role="menu" aria-label="Account options">
                  <p className="support-popover__heading">My Account</p>
                  <Link
                    to="/profile"
                    className="support-popover__item"
                    role="menuitem"
                    onClick={() => { setAccountOpen(false); closeMenu(); }}
                  >
                    <span className="support-popover__icon" aria-hidden="true" style={{ fontSize: '20px' }}>
                      👤
                    </span>
                    <span className="support-popover__body">
                      <span className="support-popover__label">Profile</span>
                      <span className="support-popover__value">View personal info</span>
                    </span>
                  </Link>
                  <Link
                    to="/loyalty"
                    className="support-popover__item"
                    role="menuitem"
                    onClick={() => { setAccountOpen(false); closeMenu(); }}
                  >
                    <span className="support-popover__icon" aria-hidden="true" style={{ fontSize: '20px' }}>
                      💎
                    </span>
                    <span className="support-popover__body">
                      <span className="support-popover__label">Loyalty</span>
                      <span className="support-popover__value">View points & rewards</span>
                    </span>
                  </Link>
                  <Link
                    to="/transactions"
                    className="support-popover__item"
                    role="menuitem"
                    onClick={() => { setAccountOpen(false); closeMenu(); }}
                  >
                    <span className="support-popover__icon" aria-hidden="true" style={{ fontSize: '20px' }}>
                      💳
                    </span>
                    <span className="support-popover__body">
                      <span className="support-popover__label">Transactions</span>
                      <span className="support-popover__value">View history</span>
                    </span>
                  </Link>
                  <button
                    type="button"
                    className="support-popover__item"
                    role="menuitem"
                    onClick={() => { setAccountOpen(false); closeMenu(); handleSignOut(); }}
                  >
                    <span className="support-popover__icon" aria-hidden="true" style={{ fontSize: '20px' }}>
                      ←
                    </span>
                    <span className="support-popover__body">
                      <span className="support-popover__label">Sign Out</span>
                      <span className="support-popover__value">Log out of account</span>
                    </span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="nav-link nav-link--cta"
              onClick={() => { closeMenu(); openLoginModal(); }}
            >
              SignIn/Register
            </button>
          )}
        </nav>
      </div>

      {/* Sign Out Confirmation Modal */}
      {showSignOutConfirm && ReactDOM.createPortal(
        <div
          className="signout-backdrop"
          onClick={cancelSignOut}
        >
          <div
            className="signout-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="signout-modal__content">
              <div className="signout-modal__icon">!</div>
              <h2 className="signout-modal__title">Sign Out</h2>
              <p className="signout-modal__description">
                You're about to sign out of your account. You can always sign back in whenever you're ready.
              </p>
            </div>

            <div className="signout-modal__actions">
              <button
                onClick={cancelSignOut}
                className="signout-modal__btn signout-modal__btn--cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmSignOut}
                className="signout-modal__btn signout-modal__btn--confirm"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}

export default Header;
